import { PAWN, type PieceType, type PromotionPiece } from '../primitives/piece.js'
import type { Square } from '../primitives/square.js'

export type CastleSide = 'king' | 'queen'

/**
 * Each variant carries exactly the fields it needs and no others: a `castle`
 * has no `captured`, a `promotion` always has a `promoteTo`. Reaching for a
 * field that cannot exist on a variant is a compile error, which is most of the
 * reason this is a union rather than one wide struct with optional fields.
 */
export type Move =
  | {
      readonly kind: 'normal'
      readonly from: Square
      readonly to: Square
      readonly piece: PieceType
      readonly captured: PieceType | null
    }
  | { readonly kind: 'doublePush'; readonly from: Square; readonly to: Square }
  | { readonly kind: 'enPassant'; readonly from: Square; readonly to: Square }
  | {
      readonly kind: 'castle'
      readonly from: Square
      readonly to: Square
      readonly side: CastleSide
      readonly rookFrom: Square
      readonly rookTo: Square
    }
  | {
      readonly kind: 'promotion'
      readonly from: Square
      readonly to: Square
      readonly promoteTo: PromotionPiece
      readonly captured: PieceType | null
    }

export type MoveKind = Move['kind']

/** What a UI hands over: two squares, plus a choice if the move promotes. */
export type MoveIntent = {
  readonly from: Square
  readonly to: Square
  readonly promoteTo?: PromotionPiece
}

export const isCapture = (move: Move): boolean => {
  switch (move.kind) {
    case 'normal':
    case 'promotion':
      return move.captured !== null
    case 'enPassant':
      return true
    case 'doublePush':
    case 'castle':
      return false
  }
}

export const isPawnMove = (move: Move): boolean => {
  switch (move.kind) {
    case 'normal':
      return move.piece === PAWN
    case 'doublePush':
    case 'enPassant':
    case 'promotion':
      return true
    case 'castle':
      return false
  }
}

export const sameMove = (a: Move, b: Move): boolean => {
  if (a.kind !== b.kind || a.from !== b.from || a.to !== b.to) return false
  if (a.kind === 'promotion' && b.kind === 'promotion') return a.promoteTo === b.promoteTo
  return true
}

/**
 * Resolve a UI intent against the legal move list. A promotion intent without a
 * `promoteTo` deliberately fails to match, so the caller is forced to ask the
 * player which piece they want before the move can be made.
 */
export const matchIntent = (moves: readonly Move[], intent: MoveIntent): Move | null => {
  for (const move of moves) {
    if (move.from !== intent.from || move.to !== intent.to) continue
    if (move.kind === 'promotion') {
      if (move.promoteTo === intent.promoteTo) return move
      continue
    }
    return move
  }
  return null
}

/** True when a from/to pair would promote, so the UI knows to show the picker. */
export const intentNeedsPromotion = (moves: readonly Move[], from: Square, to: Square): boolean =>
  moves.some((move) => move.kind === 'promotion' && move.from === from && move.to === to)
